import type { Asset } from "@studio/shared";
import { useQueryClient } from "@tanstack/react-query";
import { Brush, RotateCcw, Undo2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button, Segmented, Sheet } from "./ui";

type Point = { x: number; y: number };
type Stroke = { points: Point[]; size: number };

const BRUSHES = [
  { value: 14, label: "Fine" },
  { value: 30, label: "Medium" },
  { value: 58, label: "Wide" },
];

/**
 * Paints visible red guidance directly onto a reference picture. Qwen Image understands these marks as
 * spatial instructions, so the exported PNG is intentionally the image plus the strokes rather than a
 * separate binary mask.
 */
export function ImageMarkupEditor({
  asset,
  open,
  onOpenChange,
  onSaved,
}: {
  asset: Asset | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (marked: Asset) => void;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const source = useRef<HTMLImageElement | null>(null);
  const strokes = useRef<Stroke[]>([]);
  const drawing = useRef<Stroke | null>(null);
  const [brush, setBrush] = useState(30);
  const [marks, setMarks] = useState(0);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  const paintStroke = (ctx: CanvasRenderingContext2D, stroke: Stroke) => {
    const [first, ...rest] = stroke.points;
    if (!first) return;
    ctx.save();
    ctx.strokeStyle = "#ff244d";
    ctx.fillStyle = "#ff244d";
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = stroke.size;
    if (rest.length === 0) {
      ctx.beginPath();
      ctx.arc(first.x, first.y, stroke.size / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.beginPath();
      ctx.moveTo(first.x, first.y);
      for (const point of rest) ctx.lineTo(point.x, point.y);
      ctx.stroke();
    }
    ctx.restore();
  };

  const redraw = () => {
    const el = canvas.current;
    const image = source.current;
    const ctx = el?.getContext("2d");
    if (!el || !image || !ctx) return;
    ctx.clearRect(0, 0, el.width, el.height);
    ctx.drawImage(image, 0, 0, el.width, el.height);
    for (const stroke of strokes.current) paintStroke(ctx, stroke);
  };

  useEffect(() => {
    if (!open || !asset || asset.kind !== "image") return;
    setReady(false);
    setMarks(0);
    strokes.current = [];
    drawing.current = null;
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      const el = canvas.current;
      if (!el) return;
      el.width = image.naturalWidth;
      el.height = image.naturalHeight;
      source.current = image;
      setReady(true);
      requestAnimationFrame(() => {
        const ctx = el.getContext("2d");
        if (!ctx) return;
        ctx.clearRect(0, 0, el.width, el.height);
        ctx.drawImage(image, 0, 0, el.width, el.height);
      });
    };
    image.onerror = () => toast.error("The picture could not be opened for drawing.");
    image.src = asset.url;
    return () => {
      image.onload = null;
      image.onerror = null;
      source.current = null;
    };
  }, [open, asset]);

  const pointFrom = (event: React.PointerEvent<HTMLCanvasElement>): Point => {
    const el = event.currentTarget;
    const box = el.getBoundingClientRect();
    return {
      x: ((event.clientX - box.left) / box.width) * el.width,
      y: ((event.clientY - box.top) / box.height) * el.height,
    };
  };

  const start = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!ready) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const scale = Math.max(event.currentTarget.width, event.currentTarget.height) / 1000;
    const stroke = { points: [pointFrom(event)], size: brush * scale };
    strokes.current.push(stroke);
    drawing.current = stroke;
    redraw();
  };

  const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const stroke = drawing.current;
    if (!stroke || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
    stroke.points.push(pointFrom(event));
    redraw();
  };

  const finish = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    drawing.current = null;
    setMarks(strokes.current.length);
  };

  const undo = () => {
    strokes.current.pop();
    setMarks(strokes.current.length);
    redraw();
  };

  const clear = () => {
    strokes.current = [];
    setMarks(0);
    redraw();
  };

  const save = async () => {
    const el = canvas.current;
    if (!el || !asset || marks === 0 || saving) return;
    setSaving(true);
    try {
      const blob = await new Promise<Blob>((resolve, reject) =>
        el.toBlob(
          (value) => (value ? resolve(value) : reject(new Error("The marked picture could not be encoded."))),
          "image/png",
        ),
      );
      const base = (asset.name ?? "reference").replace(/\.[^.]+$/, "");
      const marked = await api.upload(new File([blob], `${base}-marked.png`, { type: "image/png" }));
      await queryClient.invalidateQueries({ queryKey: ["assets"] });
      onSaved(marked);
      onOpenChange(false);
      toast.success("Marked picture added", {
        description: "Describe what Qwen should change inside the red marks.",
      });
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title="Mark the area to change"
      description="Draw around or across the region. The red marks are sent with the picture so Qwen can see exactly where to edit."
      wide
    >
      <div className="relative flex min-h-[300px] items-center justify-center overflow-hidden rounded-[var(--radius-media)] bg-black/60">
        <canvas
          ref={canvas}
          aria-label="Draw red marks on the reference picture"
          className="max-h-[62dvh] max-w-full touch-none object-contain"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={finish}
          onPointerCancel={finish}
        />
        {!ready ? <span className="absolute text-sm text-mute">Opening picture…</span> : null}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="mr-auto flex items-center gap-2">
          <Brush className="size-4 text-mute" aria-hidden />
          <Segmented size="sm" value={brush} onChange={setBrush} options={BRUSHES} />
        </div>
        <Button size="sm" variant="quiet" onClick={undo} disabled={marks === 0 || saving}>
          <Undo2 className="size-4" aria-hidden />
          Undo
        </Button>
        <Button size="sm" variant="quiet" onClick={clear} disabled={marks === 0 || saving}>
          <RotateCcw className="size-4" aria-hidden />
          Clear
        </Button>
        <Button size="sm" variant="primary" onClick={save} disabled={!ready || marks === 0 || saving}>
          {saving ? "Saving…" : "Use marked picture"}
        </Button>
      </div>
    </Sheet>
  );
}
